import { CircleCheck, CircleX, Info, TriangleAlert } from "lucide-react";
import React, { ElementType, FC, ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgIconButton, IgTypography } from "@intelligems/ig-theme";
import { getTextColor } from "@intelligems/ig-utils";
import { Grid } from "@mui/material";
import { AlertColor } from "@mui/material/Alert/Alert";
import { Theme } from "@mui/material/styles";
import { SxProps } from "@mui/system";
import { useGetOrg } from "@src/api/organizations/get-organization";
import {
  dismissAlert,
  getIsAlertDismissed,
} from "@src/store/slices/appSettings";
import { theme } from "@src/theme";
import { openNewTab } from "@src/utils/openNewTab";

interface IgAlertSimpleInvertedProps {
  severity: AlertColor;
  message: ReactNode | string;
  alertKey: string;
  sx?: SxProps<Theme>;
  dismissId?: string;
  onDismiss?: () => void;
  link?: string;
  colorBackground?: boolean;
}

const IconMap: Record<AlertColor, ElementType> = {
  error: CircleX,
  warning: TriangleAlert,
  success: CircleCheck,
  info: Info,
};

const ColorMap: Record<AlertColor, string> = {
  error: theme.igBackgroundColors.tonal.error,
  warning: theme.igBackgroundColors.tonal.warning,
  info: theme.igBackgroundColors.tonal.info,
  success: theme.igBackgroundColors.tonal.success,
};

// Since this component needs access to redux we can't add to ig-theme
export const IgAlertSimpleInverted: FC<IgAlertSimpleInvertedProps> = ({
  severity,
  message,
  alertKey,
  sx,
  dismissId,
  onDismiss,
  link,
  colorBackground,
}) => {
  const Icon: ElementType = IconMap[severity];
  const dispatch = useDispatch();
  const org = useGetOrg();
  const _isDismissed = useSelector(
    getIsAlertDismissed(org.data?.org.id, dismissId)
  );

  const IconColorMap: Record<AlertColor, string> = {
    error: theme.igIconColors.error,
    warning: theme.igIconColors.warning,
    info: theme.igIconColors.info,
    success: theme.igIconColors.success,
  };

  const isDismissed = dismissId ? _isDismissed : false;

  if (isDismissed) {
    return null;
  }

  return (
    <Grid
      id={alertKey}
      direction={"row"}
      container
      item
      alignItems={"center"}
      flexGrow={8}
      sm
      sx={{
        minHeight: "50px",
        color: getTextColor(ColorMap[severity]),
        backgroundColor: ColorMap[severity],
        borderRadius: "8px",
        padding: "8px 15px 8px 15px",
        textAlign: "left",
        ...sx,
      }}
    >
      <Grid item mr={2} sx={{ height: "24px" }}>
        <Icon color={IconColorMap[severity]} size={20} />
      </Grid>
      <Grid item sm>
        {typeof message === "string" ? (
          <IgTypography>{message}</IgTypography>
        ) : (
          <span>{message}</span>
        )}
      </Grid>
      {link && (
        <IgTypography
          igStyle={"link"}
          onClick={() => {
            openNewTab(link);
          }}
        >
          Learn more
        </IgTypography>
      )}
      {dismissId && (
        <Grid item>
          <IgIconButton
            onClick={() => {
              dispatch(
                dismissAlert({ orgId: org.data.org.id, alertId: dismissId })
              );
              if (onDismiss) {
                onDismiss();
              }
            }}
            iconType={"clear"}
          />
        </Grid>
      )}
    </Grid>
  );
};
